import React, { useState, useEffect, useCallback, useRef } from 'react';
import { Outlet, useNavigate, useLocation } from 'react-router-dom';
import { Sidebar } from './Sidebar';
import { useLanguage } from '../contexts/LanguageContext';
import RewardToastHub from '@/features/gamification/components/RewardToastHub';
import { OnboardingTour, getWelcomeStorageKey, WELCOME_VERSION } from './OnboardingTour';
import { SettingsModal } from './SettingsModal';
import { ProfileSetupModal } from './ProfileSetupModal';
import { fetchCurrentProfile, type UserProfile } from '../services/profileService';
import { getCurrentUser, signOut, subscribeToAuthChanges } from '@/services/authService';

// ChatBot is loaded on-demand with error handling to avoid crashing the shell if the module fails to load

const SETTINGS_KEY = "msd_settings_v1";
const DEFAULT_RETENTION_HOURS = 24;

export const Layout: React.FC = () => {
  const navigate = useNavigate();
  const location = useLocation();
  const { language } = useLanguage();
  const isChatAllowed = location.pathname.includes('dashboard');

  const [isSettingsOpen, setIsSettingsOpen] = useState(false);
  const [showTour, setShowTour] = useState(false);
  const hasShownWelcome = useRef(false);
  const [chatLoaded, setChatLoaded] = useState(false);
  const [ChatBotComponent, setChatBotComponent] = useState<React.ComponentType | null>(null);
  const [chatError, setChatError] = useState<Error | null>(null);
  const [profile, setProfile] = useState<UserProfile | null>(null);
  const [isProfileModalOpen, setIsProfileModalOpen] = useState(false);
  const [forceProfileCompletion, setForceProfileCompletion] = useState(false);

  // Determine active tab based on path
  const getActiveTab = () => {
    const path = location.pathname;
    if (path.includes('dashboard')) return 'dashboard';
    if (path.includes('weekly-plan')) return 'weekly-plan';
    if (path.includes('analytics')) return 'analytics';
    if (path.includes('grades')) return 'grades';
    if (path.includes('projects')) return 'projects';
    if (path.includes('leaderboards')) return 'leaderboards';
    if (path.includes('study-buddies')) return 'study-buddies';
    return 'dashboard';
  };

  const [isSidebarExpanded, setIsSidebarExpanded] = useState(false);

  const handleLogout = async () => {
    const result = await signOut();
    if (result.error) {
      console.error('Logout failed', result.error);
    }
    navigate("/");
  };

  const refreshProfile = useCallback(async () => {
    try {
      const userResult = await getCurrentUser();
      if (userResult.error) throw userResult.error;
      const user = userResult.data;
      if (!user) {
        setProfile(null);
        setIsProfileModalOpen(false);
        setForceProfileCompletion(false);
        return null;
      }
      const currentProfile = await fetchCurrentProfile();
      setProfile(currentProfile);
      const needsProfile = !currentProfile?.fullName?.trim();
      setForceProfileCompletion(needsProfile);
      setIsProfileModalOpen(needsProfile);
      return currentProfile;
    } catch (err) {
      console.error('profile refresh failed', err);
      setProfile(null);
      setIsProfileModalOpen(false);
      setForceProfileCompletion(false);
      return null;
    }
  }, []);

  // Load settings/onboarding logic (migrated from Dashboard)
  useEffect(() => {
    (async () => {
      try {
        const userResult = await getCurrentUser();
        if (userResult.error) throw userResult.error;
        const user = userResult.data;

        // Onboarding logic: trust Supabase metadata for authenticated users, local storage for guests
        let shouldShowWelcome = false;
        if (user) {
          const metaVersion = user.user_metadata?.welcome_version;
          shouldShowWelcome = metaVersion !== WELCOME_VERSION;
        } else {
          const anonKey = getWelcomeStorageKey();
          shouldShowWelcome = !localStorage.getItem(anonKey);
        }

        if (shouldShowWelcome && !hasShownWelcome.current) {
          setShowTour(true);
          hasShownWelcome.current = true;
        }
      } catch (err) {
        // Fallback for anon/error
        if (!localStorage.getItem(getWelcomeStorageKey())) {
          setShowTour(true);
          hasShownWelcome.current = true;
        }
      }
    })();

    if (!isChatAllowed) {
      setChatLoaded(false);
      setChatBotComponent(null);
      setChatError(null);
      return undefined;
    }
    const timer = setTimeout(() => setChatLoaded(true), 2000);
    return () => clearTimeout(timer);
  }, [isChatAllowed, showTour]);

  // Chat State lifted to Layout
  const [isChatOpen, setIsChatOpen] = useState(false);

  useEffect(() => {
    if (!chatLoaded || ChatBotComponent || chatError) return;
    let cancelled = false;
    import('./ChatBot')
      .then((module) => {
        if (cancelled) return;
        setChatBotComponent(() => module.ChatBot);
      })
      .catch((err) => {
        if (cancelled) return;
        console.error('ChatBot load failed', err);
        setChatError(err instanceof Error ? err : new Error('ChatBot load failed'));
      });
    return () => {
      cancelled = true;
    };
  }, [ChatBotComponent, chatError, chatLoaded]);

  // Listen for toggle requests from children
  useEffect(() => {
    const handleToggle = () => setIsChatOpen(prev => !prev);
    window.addEventListener('msd-toggle-chat', handleToggle);
    return () => window.removeEventListener('msd-toggle-chat', handleToggle);
  }, []);

  // Auto-close chat on route change
  useEffect(() => {
    setIsChatOpen(false);
  }, [location.pathname, location.search]);

  useEffect(() => {
    refreshProfile();
    const authListener = subscribeToAuthChanges(() => {
      refreshProfile();
    });
    return () => {
      authListener?.unsubscribe();
    };
  }, [refreshProfile]);

  useEffect(() => {
    const handleProfileUpdated = (event: Event) => {
      const detail = (event as CustomEvent<UserProfile | null>).detail;
      if (detail) {
        setProfile(detail);
        const needsProfile = !detail.fullName?.trim();
        setForceProfileCompletion(needsProfile);
        setIsProfileModalOpen(needsProfile);
      } else {
        refreshProfile();
      }
    };
    window.addEventListener('msd-profile-updated', handleProfileUpdated);
    return () => window.removeEventListener('msd-profile-updated', handleProfileUpdated);
  }, [refreshProfile]);

  const handleProfileSaved = (nextProfile: UserProfile) => {
    setProfile(nextProfile);
    setForceProfileCompletion(false);
    setIsProfileModalOpen(false);
  };

  return (
    <div className="min-h-screen bg-subtle-texture dark:bg-slate-950 transition-colors duration-200 overflow-x-hidden">
      <Sidebar
        activeTab={getActiveTab()}
        onNavigate={(path) => navigate(path)}
        onLogout={handleLogout}
        onOpenSettings={() => setIsSettingsOpen(true)}
        isExpanded={isSidebarExpanded}
        onExpandedChange={setIsSidebarExpanded}
      />

      <main
        className={`min-h-screen w-full transition-all duration-200 ease-out 
          ${language === 'ar'
            ? (isSidebarExpanded ? 'lg:pr-64' : 'lg:pr-[72px]')
            : (isSidebarExpanded ? 'lg:pl-64' : 'lg:pl-[72px]')
          }
        `}
      >
        <div className="w-full px-3 sm:px-6 lg:px-8 pb-[calc(96px+env(safe-area-inset-bottom))]">
          <Outlet />
        </div>
      </main>

      {/* Global Modals & Features */}
      <RewardToastHub />

      {showTour && (
        <OnboardingTour onFinish={() => setShowTour(false)} />
      )}

      {isSettingsOpen && (
        <SettingsModal
          isOpen={isSettingsOpen}
          onClose={() => setIsSettingsOpen(false)}
        />
      )}

      <ProfileSetupModal
        isOpen={isProfileModalOpen}
        initialProfile={profile}
        onClose={() => setIsProfileModalOpen(false)}
        onProfileSaved={handleProfileSaved}
        forceCompletion={forceProfileCompletion}
      />

      <div style={{ display: isSettingsOpen ? 'none' : 'block' }}>
        {ChatBotComponent && !chatError && (
          // @ts-ignore - Dynamic import typing issue
          <ChatBotComponent isOpen={isChatOpen} onToggle={() => setIsChatOpen(prev => !prev)} />
        )}
      </div>
    </div>
  );
};
